<script setup>
import {
  computed,
  onMounted,
  nextTick,
  onUnmounted,
  useTemplateRef,
  inject,
} from 'vue';
import { useWindowSize, useElementBounding, useScrollLock } from '@vueuse/core';

import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
  anchorTop: { type: Number, default: null },
  anchorBottom: { type: Number, default: null },
  anchorLeft: { type: Number, default: null },
  anchorRight: { type: Number, default: null },
});

const emit = defineEmits(['close']);

const elementToLock = inject('contextMenuElementTarget', null);

const menuRef = useTemplateRef('menuRef');

const scrollLockElement = computed(() => {
  if (!elementToLock?.value) return null;
  return elementToLock.value?.$el;
});

const isLocked = useScrollLock(scrollLockElement);

const { width: windowWidth, height: windowHeight } = useWindowSize();
const { width: menuWidth, height: menuHeight } = useElementBounding(menuRef);

const calculatePosition = (x, y, menuW, menuH, windowW, windowH) => {
  const PADDING = 16;
  // Initial position
  let left = x;
  let top = y;
  // Boundary checks
  const isOverflowingRight = left + menuW > windowW - PADDING;
  const isOverflowingBottom = top + menuH > windowH - PADDING;
  // Adjust position if overflowing
  if (isOverflowingRight) left = windowW - menuW - PADDING;
  if (isOverflowingBottom) top = windowH - menuH - PADDING;
  return {
    left: Math.max(PADDING, left),
    top: Math.max(PADDING, top),
  };
};

const position = computed(() => {
  const PADDING = 12;
  const GAP = 12;

  // Mantenerlo invisible hasta conocer sus dimensiones reales.
  if (
    !menuRef.value ||
    menuWidth.value <= 0 ||
    menuHeight.value <= 0
  ) {
    return {
      top: `${props.y}px`,
      left: `${props.x}px`,
      visibility: 'hidden',
    };
  }

  const hasAnchor = [
    props.anchorTop,
    props.anchorBottom,
    props.anchorLeft,
    props.anchorRight,
  ].every(value => Number.isFinite(value));

  if (hasAnchor) {
    const anchorWidth = props.anchorRight - props.anchorLeft;
    const anchorCenterY =
      props.anchorTop + (props.anchorBottom - props.anchorTop) / 2;

    const spaceAbove = Math.max(
      0,
      props.anchorTop - GAP - PADDING
    );

    const spaceBelow = Math.max(
      0,
      windowHeight.value - props.anchorBottom - GAP - PADDING
    );

    // Regla visual estable:
    // mitad superior -> menú debajo
    // mitad inferior -> menú encima
    let placeAbove = anchorCenterY >= windowHeight.value / 2;

    // Si el lado elegido es demasiado pequeño, usar el lado con más espacio.
    if (placeAbove && spaceAbove < 120 && spaceBelow > spaceAbove) {
      placeAbove = false;
    }

    if (!placeAbove && spaceBelow < 120 && spaceAbove > spaceBelow) {
      placeAbove = true;
    }

    const availableHeight = Math.max(
      1,
      placeAbove ? spaceAbove : spaceBelow
    );

    const renderedHeight = Math.min(
      menuHeight.value,
      availableHeight
    );

    let left =
      props.anchorLeft +
      anchorWidth / 2 -
      menuWidth.value / 2;

    left = Math.max(
      PADDING,
      Math.min(
        left,
        windowWidth.value - menuWidth.value - PADDING
      )
    );

    let top = placeAbove
      ? props.anchorTop - GAP - renderedHeight
      : props.anchorBottom + GAP;

    top = Math.max(
      PADDING,
      Math.min(
        top,
        windowHeight.value - renderedHeight - PADDING
      )
    );

    return {
      top: `${top}px`,
      left: `${left}px`,
      maxHeight: `${availableHeight}px`,
      overflowY:
        menuHeight.value > availableHeight ? 'auto' : 'visible',
      overscrollBehavior: 'contain',
      visibility: 'visible',
    };
  }

  const { left, top } = calculatePosition(
    props.x,
    props.y,
    menuWidth.value,
    menuHeight.value,
    windowWidth.value,
    windowHeight.value
  );

  return {
    top: `${top}px`,
    left: `${left}px`,
    visibility: 'visible',
  };
});

onMounted(() => {
  isLocked.value = true;
  nextTick(() => menuRef.value?.focus());
});

const handleClose = () => {
  isLocked.value = false;
  emit('close');
};

const handleFocusOut = event => {
  // Keep the menu open while focus stays inside it (e.g. the label search
  // input); close it once focus leaves the menu entirely.
  if (menuRef.value?.contains(event.relatedTarget)) {
    return;
  }
  handleClose();
};

onUnmounted(() => {
  isLocked.value = false;
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      ref="menuRef"
      class="fixed outline-none z-[9999] cursor-pointer"
      :style="position"
      tabindex="0"
      @focusout="handleFocusOut"
    >
      <slot />
    </div>
  </TeleportWithDirection>
</template>
